import {t} from '@github-ui/swp-core/lib/utils/localization'
import type {SupportedLocale} from '@github-ui/swp-core/lib/utils/localization'
import type {BannerDescriptor, Universe26Artwork} from './marketing-global-banner.types'

// `/home` also serves the homepage.
const HOMEPAGE_PATHS = ['/', '/home']

const universe26Link = (uid: string) =>
  `https://githubuniverse.com/?utm_source=github&utm_medium=banner&utm_campaign=${uid}`

function universe26Banner({
  uid,
  link = universe26Link(uid),
  feature_flag,
  artwork,
  ...rest
}: Pick<
  BannerDescriptor,
  'uid' | 'title' | 'link_label' | 'feature_flag' | 'countdown' | 'live_highlight' | 'excluded_paths'
> & {
  link?: string
  artwork: Universe26Artwork
}): BannerDescriptor {
  return {
    uid,
    link,
    // The on-demand artwork is drawn for a black banner.
    mode: artwork === 'on-d' ? 'dark' : 'light',
    custom_design: 'universe_26',
    artwork,
    feature_flag,
    ...rest,
  }
}

/**
 * To add a banner, append a descriptor here and register its `feature_flag` in the `jsFeatureFlags`
 * allowlist in `@github-ui/feature-flags`, or it will never be enabled client-side.
 *
 * Universe 26 campaigns are listed newest first, so a later campaign wins if an earlier flag is left on.
 */
export function getBannerRegistry(locale?: SupportedLocale): BannerDescriptor[] {
  return [
    universe26Banner({
      uid: 'gb_post_on-d_uni26',
      title: "Relive Universe '26 on-demand.",
      link_label: 'Watch sessions',
      feature_flag: 'universe26_banners_post_on-d',
      artwork: 'on-d',
      excluded_paths: HOMEPAGE_PATHS,
    }),
    universe26Banner({
      uid: 'gb_day2_key_uni26',
      title: 'Day 2: Keynote now streaming!',
      link_label: 'Watch now',
      feature_flag: 'universe26_banners_day2_key',
      artwork: 'keyn',
      excluded_paths: HOMEPAGE_PATHS,
    }),
    universe26Banner({
      uid: 'gb_on-d_uni26',
      title: 'Missed Day 1: Opening Keynote?',
      // TODO: replace with the K2k link once supplied.
      link_label: 'Catch up',
      feature_flag: 'universe26_banners_on-d',
      artwork: 'on-d',
      excluded_paths: HOMEPAGE_PATHS,
    }),
    universe26Banner({
      uid: 'gb_keyn_uni26',
      title: 'Day 1: Opening Keynote is now LIVE',
      live_highlight: 'LIVE',
      link_label: 'Watch now',
      feature_flag: 'universe26_banners_keyn',
      artwork: 'keyn',
    }),
    universe26Banner({
      uid: 'gb_tom_uni26',
      title: 'Day 1: Opening Keynote begins in',
      link_label: 'Register virtually',
      feature_flag: 'universe26_banners_tom',
      artwork: 'tom',
      countdown: {
        // 9am Pacific on Oct 28, 2026 (PDT, UTC-7).
        to: '2026-10-28T09:00:00-07:00',
        accessible_label: 'Day 1: Opening Keynote begins October 28 at 9 AM Pacific Time',
      },
    }),
    {
      uid: 'gb_virt_uni26',
      title: 'Join Universe virtually, from anywhere.',
      link: universe26Link('gb_virt_uni26'),
      link_label: 'Save your seat',
      mode: 'light',
      custom_design: 'universe_26',
      feature_flag: 'universe26_banners_virt',
    },
    {
      uid: 'desktop_copilot_app',
      title: 'Looking for an agent-driven desktop experience?',
      link: 'https://github.com/features/ai/github-app?utm_source=github&utm_medium=banner&utm_campaign=desktop_copilot_app',
      link_label: 'Download the GitHub Copilot app',
      mode: 'dark',
      allowed_paths: ['/apps/desktop'],
      feature_flag: 'site_banner_desktop_copilot_app',
    },
    {
      uid: 'security_webinar',
      title: 'Secret Leaks: Trends, Risks, and How to Respond',
      link: 'https://github.registration.goldcast.io/webinar/77c2c07e-1cd1-4f17-9f4c-7d1500a658e9?utm_source=gh_web_banner&utm_medium=web&utm_campaign=SecurityPodJuly28',
      link_label: 'Register now',
      mode: 'dark',
      allowed_paths: [
        '/security',
        '/security/advanced-security/secret-protection',
        '/security/advanced-security/code-security',
      ],
      feature_flag: 'site_global_banner_security_webinar',
    },
    {
      uid: 'dev_days_attendee',
      title: t(
        'Dev Days are here! Join an in-person event near you to learn more about the GitHub Copilot app and Copilot CLI with your community.',
        locale,
      ),
      link: 'https://gh.io/join-dev-days?utm_source=github&utm_medium=banner&utm_campaign=dev_days_attendee',
      link_label: 'Find an event',
      mode: 'dark',
      allowed_paths: [
        '/features/copilot',
        '/features/copilot/cli',
        '/features/copilot/plans',
        '/features/ai/github-app',
        '/features',
        '/resources',
      ],
      feature_flag: 'site_global_banner_dev_days_attendee',
    },
  ]
}
